import { Component, inject, OnInit, OnDestroy, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Notification } from '../models/notification.model';
import { Observable, Subscription } from 'rxjs';
import moment from 'moment-timezone';
import { TranslationService } from '../translations/services/translation.service';
import { NotificationService } from '../services/notification.service';
import { NotificationPreferencesService } from '../services/notification-preferences.service';
import { DateFormatService } from '../services/date-format.service';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatMenuModule],
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss']
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  @ViewChild('notificationPanel') notificationPanel: ElementRef;

  unreadCount$: Observable<number>;
  notifications$: Observable<Notification[]>;
  isPanelOpen = false;
  displayCount: string = '0';
  displayedNotifications: Notification[] = [];
  hasNotifications = false;
  isScrollable = false;
  notificationsFeatureEnabled = true;

  readonly MAX_DISPLAY_NOTIFICATIONS = 3;

  public translationService = inject(TranslationService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private readonly notificationPreferencesService = inject(NotificationPreferencesService);
  private readonly dateFormatService = inject(DateFormatService);

  private readonly subscriptions = new Subscription();

  private static readonly CAM_MESSAGE_KEYS: Record<Notification['action'], string> = {
    CREATED: 'notificationCamSpecCreatedText',
    UPDATED: 'notificationCamSpecUpdatedText',
    DELETED: 'notificationCamSpecDeletedText'
  };

  ngOnInit(): void {
    this.notificationsFeatureEnabled = this.notificationPreferencesService.isFeatureEnabled;

    this.subscriptions.add(
      this.notificationPreferencesService.notificationsFeatureEnabled$.subscribe((enabled) => {
        this.notificationsFeatureEnabled = enabled;
        if (!enabled) {
          this.closePanel();
        }
      })
    );

    this.unreadCount$ = this.notificationService.unreadCount$;
    this.notifications$ = this.notificationService.notifications$;

    this.subscriptions.add(
      this.unreadCount$.subscribe(() => {
        this.displayCount = this.notificationService.getDisplayCount();
      })
    );

    this.subscriptions.add(
      this.notifications$.subscribe((notifications) => {
        const sorted = this.getSortedNotifications(notifications || []);
        this.hasNotifications = sorted.length > 0;
        this.isScrollable = sorted.length > this.MAX_DISPLAY_NOTIFICATIONS;
        this.displayedNotifications = sorted;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  togglePanel(event: MouseEvent): void {
    if (!this.notificationsFeatureEnabled) {
      return;
    }

    event.stopPropagation();
    this.isPanelOpen = !this.isPanelOpen;
  }

  closePanel(): void {
    this.isPanelOpen = false;
  }

  markAsRead(notification: Notification, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (!notification.isSeen) {
      this.notificationService.markAsRead(notification.notificationId, 'BACKOFFICE').subscribe({
        error: (err) => console.error('Failed to mark as read:', err)
      });
    }
  }

  dismissAll(): void {
    this.notificationService.dismissAll();
  }

  navigateToNotification(notification: Notification, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (notification.link) {
      this.closePanel();
      // Use the Angular router instead of a hard window.location navigation
      // so we stay inside the running SPA (dealer context, auth state, etc.)
      // - a hard reload to a deep link like /home/specifications/<id> can
      // come back blank if the dev/hosting server isn't set up to fall back
      // to index.html for client-side routes.
      this.router.navigateByUrl(notification.link);
    }
  }

  private notificationContent(notification: Notification): { before: string; after?: string } {
    const fallback = { before: notification.link ? '' : notification.message };
    if (notification.resourceType !== 'CAM_SPEC' || !notification.resourceName) {
      return fallback;
    }
    const key = NotificationBellComponent.CAM_MESSAGE_KEYS[notification.action];
    const template = key && this.translationService.translate(key);
    if (!template || template === key || !template.includes('{0}') || !template.includes('{1}')) {
      return fallback;
    }
    const date = moment(notification.createdAt);
    if (!date.isValid()) {
      return fallback;
    }
    const parts = template.split('{LINK}');
    if (!!notification.link !== (parts.length === 2)) {
      return fallback;
    }
    const formattedDate = date.format(this.dateFormatService.currentFormat);
    const interpolate = (part: string) =>
      part.replace(/\{([01])\}/g, (_, placeholder: string) => (placeholder === '0' ? notification.resourceName : formattedDate));
    return { before: interpolate(parts[0]), after: parts.length === 2 ? interpolate(parts[1]) : undefined };
  }

  protected messageBeforeLink(notification: Notification): string {
    return this.notificationContent(notification).before;
  }

  protected messageAfterLink(notification: Notification): string {
    return this.notificationContent(notification).after ?? '';
  }

  protected hasLocalizedLink(notification: Notification): boolean {
    return this.notificationContent(notification).after !== undefined;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.notificationPanel && !this.notificationPanel.nativeElement.contains(event.target)) {
      this.closePanel();
    }
  }

  getSortedNotifications(notifications: Notification[]): Notification[] {
    if (!notifications) return [];
    return [...notifications].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);

    const isSameDay = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();

    if (isSameDay) {
      if (diffMinutes < 1) return this.translationService.translate('justNowText');
      if (diffMinutes < 60) return this.translationService.translate('minutesAgoAbbrText').replace('{0}', diffMinutes.toString());
      return this.translationService.translate('hoursAgoAbbrText').replace('{0}', diffHours.toString());
    }

    // Not today - use calendar-day difference (not a raw 24h count) so
    // "yesterday" means the previous calendar day, not "within 24-48 hours".
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const calendarDaysAgo = Math.round((startOfToday.getTime() - startOfDate.getTime()) / (1000 * 60 * 60 * 24));

    if (calendarDaysAgo === 1) return this.translationService.translate('yesterdayText');
    if (calendarDaysAgo >= 2 && calendarDaysAgo <= 6) {
      return this.translationService.translate('daysAgoText').replace('{0}', calendarDaysAgo.toString());
    }
    return date.toLocaleDateString();
  }

  getIconForAction(action: Notification['action']): string {
    switch (action) {
      case 'CREATED':
        return 'add_circle_outline';
      case 'DELETED':
        return 'delete_outline';
      case 'UPDATED':
      default:
        return 'assignment_turned_in';
    }
  }
}
